<template>
  <div class="bim-params">
    <div class="top-bar">
      <PageTitle hideMarginLeft>БИМ Параметры</PageTitle>
    </div>

    <div class="header">
      <div class="header__info">
        <q-checkbox
          v-if="params.length"
          :model-value="allSelected"
          @update:model-value="toggleAllSelection"
          size="sm"
          dense
          label="Выбрать все"
          style="margin-right: 10px;"
        />
        <span class="header__count">Всего параметров: {{ params.length }}</span>
        <q-btn
          v-if="selectedParams.length > 0"
          icon="delete"
          flat
          dense
          round
          size="sm"
          color="negative"
          @click="confirmDeleteSelected"
        >
          <q-tooltip>Удалить выбранные ({{ selectedParams.length }})</q-tooltip>
        </q-btn>
      </div>
      <CustomButton
        class="header__create-btn"
        @click="openDialog()"
      >
        <span>Добавить параметр</span>
        <q-icon name="add" class="header__create-btn-icon" size="16px" />
      </CustomButton>
    </div>

    <CustomTable 
      title="Параметры" 
      :rowsData="params" 
      :columns="columns" 
      :loading="isLoading"
      :small="true"
      :show-search="true"
      :custom-body="true"
      :sticky-header="true"
      :height="300"
    >
      <template #customBody="props">
        <q-tr>
          <custom-table-cell align="center" vertical-align="center" style="width: 50px;">
            <q-checkbox
              :model-value="selectedParams.includes(props.row.bim_param_id)"
              @update:model-value="(val) => toggleParamSelection(props.row.bim_param_id, val)"
              size="xs"
              dense
            />
          </custom-table-cell>

          <custom-table-cell align="center" vertical-align="center" style="width: 80px;">
            {{ props.row.bim_param_id }}
          </custom-table-cell>

          <custom-table-cell align="left" vertical-align="center" style="min-width: 250px;">
            {{ props.row.param_name }}
          </custom-table-cell>

          <custom-table-cell align="left" vertical-align="center" style="min-width: 200px;">
            {{ props.row.buffer_property_name || '-' }}
          </custom-table-cell>

          <custom-table-cell align="center" vertical-align="center" style="width: 120px;">
            <div class="action-buttons">
              <q-btn
                icon="edit"
                flat
                dense
                round
                size="xs"
                color="primary"
                @click.stop="openDialog(props.row)"
              >
                <q-tooltip>Редактировать</q-tooltip>
              </q-btn>
              <q-btn
                icon="delete"
                flat
                dense
                round
                size="xs"
                color="negative"
                @click.stop="confirmDelete(props.row)"
              >
                <q-tooltip>Удалить</q-tooltip>
              </q-btn>
            </div>
          </custom-table-cell>
        </q-tr>
      </template>
    </CustomTable>

    <!-- Прогресс-бар для группового удаления -->
    <q-dialog v-model="deletionProgressDialog" persistent>
      <q-card style="min-width: 400px">
        <q-card-section>
          <div class="text-h6">Удаление записей</div>
        </q-card-section>
        
        <q-card-section>
          <div style="margin-bottom: 10px;">
            Удалено: {{ deletionProgress.current }} из {{ deletionProgress.total }}
          </div>
          <q-linear-progress
            :value="deletionProgress.current / deletionProgress.total"
            color="primary"
            size="20px"
            class="q-mt-sm"
          >
            <div class="absolute-full flex flex-center">
              <q-badge color="white" text-color="primary" :label="`${Math.round((deletionProgress.current / deletionProgress.total) * 100)}%`" />
            </div>
          </q-linear-progress>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Диалог для добавления/редактирования -->
    <q-dialog v-model="dialog" persistent>
      <q-card class="param-modal">
        <q-card-section class="param-modal__header">
          <div class="param-modal__title">{{ editing ? 'Редактировать параметр' : 'Добавить параметр' }}</div>
          <q-btn icon="close" flat round dense v-close-popup class="param-modal__close" />
        </q-card-section>

        <q-card-section class="param-modal__content">
          <q-form @submit.prevent="save" class="param-form">
            <div class="param-form__section">
              <div class="param-form__item">
                <CustomInput
                  label="Название параметра"
                  type="text"
                  :required="true"
                  :value="form.param_name"
                  @change_input="(value) => form.param_name = value"
                  autofocus
                />
              </div>
              <div class="param-form__item">
                <CustomSelect
                  label="Свойство"
                  :options="bufferProperties"
                  option-label-key="property_name"
                  option-value-key="buffer_property_id"
                  :value="form.buffer_property_id"
                  @change="(value) => form.buffer_property_id = value"
                  :loading="isLoadingBufferProperties"
                  placeholder="Выберите свойство"
                  :clearable="true"
                />
              </div>
            </div>

            <div class="param-modal__footer">
              <CustomButton
                @click="dialog = false"
                :disabled="isSaving"
                outlined
              >
                Отмена
              </CustomButton>
              <CustomButton
                type="submit"
                :loading="isSaving"
              >
                {{ editing ? 'Сохранить' : 'Создать' }}
              </CustomButton>
            </div>
          </q-form>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Диалог подтверждения удаления -->
    <q-dialog v-model="confirmDialog.show" persistent>
      <q-card style="min-width: 350px">
        <q-card-section>
          <div class="text-h6">{{ confirmDialog.title }}</div>
        </q-card-section>

        <q-card-section>
          {{ confirmDialog.message }}
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Отмена" color="grey" @click="confirmDialog.show = false" />
          <q-btn unelevated label="Удалить" color="negative" @click="onConfirmDelete" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import CustomInput from "@/components/form/CustomInput";
import CustomButton from "@/components/common/CustomButton.vue";
import CustomTable from "@/components/common/CustomTable";
import CustomTableCell from "@/components/common/CustomTable/custom-table-cell";
import CustomSelect from "@/components/form/CustomSelect";
import PageTitle from "@/components/common/PageTitle.vue";
import {
  getBimParams,
  saveBimParam,
  deleteBimParam,
  getBufferProperties
} from "@/views/BimParams/services";

export default {
  name: "BimParams",
  components: {
    CustomInput,
    CustomButton,
    CustomTable,
    CustomTableCell,
    CustomSelect,
    PageTitle
  },
  data() {
    return {
      params: [],
      bufferProperties: [],
      isLoading: false,
      isLoadingBufferProperties: false,
      dialog: false,
      editing: null,
      form: {
        param_name: '',
        buffer_property_id: null
      },
      isSaving: false,
      searchQuery: '',
      selectedParams: [],
      deletionProgressDialog: false,
      deletionProgress: {
        current: 0,
        total: 0
      },
      pagination: {
        page: 1,
        rowsPerPage: 20
      },
      confirmDialog: {
        show: false,
        title: '',
        message: '',
        onConfirm: null
      },
      columns: [
        {
          name: "checkbox",
          field: "checkbox",
          label: "",
          align: "center",
          style: "width: 50px",
        },
        {
          name: "bim_param_id",
          field: "bim_param_id",
          label: "ID",
          align: "center",
          sortable: true,
          style: "width: 80px",
        },
        {
          name: "param_name",
          field: "param_name",
          label: "Название параметра",
          align: "left",
          sortable: true,
          style: "min-width: 250px",
        },
        {
          name: "buffer_property_name",
          field: "buffer_property_name",
          label: "Свойство",
          align: "left",
          sortable: true,
          style: "min-width: 200px",
        },
        {
          name: "actions",
          field: "actions",
          label: "Действия",
          align: "center",
          style: "width: 120px",
        },
      ],
    };
  },
  computed: {
    filteredParams() {
      if (!this.searchQuery) return this.params;
      const query = this.searchQuery.toLowerCase();
      return this.params.filter(param => 
        param.param_name.toLowerCase().includes(query) ||
        param.bim_param_id.toString().includes(query) ||
        (param.buffer_property_name && param.buffer_property_name.toLowerCase().includes(query))
      );
    },
    allSelected() {
      return this.params.length > 0 && this.selectedParams.length === this.params.length;
    }
  },
  methods: {
    async loadData() {
      this.isLoading = true;
      const res = await getBimParams();
      this.isLoading = false;
      if (!res) return;
      this.params = res;
    },
    async loadBufferProperties() {
      if (this.bufferProperties.length > 0) return;
      this.isLoadingBufferProperties = true;
      const res = await getBufferProperties();
      this.isLoadingBufferProperties = false;
      if (res) {
        this.bufferProperties = res;
      }
    },
    async openDialog(param = null) {
      // Загружаем buffer properties
      await this.loadBufferProperties();
      
      this.editing = param;
      if (param) {
        this.form = {
          param_name: param.param_name,
          buffer_property_id: param.buffer_property_id
        };
      } else {
        this.form = {
          param_name: '',
          buffer_property_id: null
        };
      }
      this.dialog = true;
    },
    async save() {
      if (!this.form.param_name) {
        return;
      }

      this.isSaving = true;
      const data = {
        bim_param_id: this.editing?.bim_param_id || null,
        param_name: this.form.param_name,
        buffer_property_id: this.form.buffer_property_id || null
      };

      const res = await saveBimParam(data);
      this.isSaving = false;

      if (res) {
        this.showSucceedSnackbar(this.editing ? 'Параметр обновлен' : 'Параметр добавлен');
        this.dialog = false;
        
        // Обновляем данные из БД без loader (реактивное обновление)
        const result = await getBimParams();
        if (result) {
          this.params = result;
        }
      }
    },
    confirmDelete(param) {
      this.confirmDialog = {
        show: true,
        title: 'Подтверждение',
        message: `Вы уверены, что хотите удалить параметр "${param.param_name}"?`,
        onConfirm: async () => {
          await this.deleteHandler(param.bim_param_id);
        }
      };
    },
    async deleteHandler(bim_param_id) {
      const res = await deleteBimParam(bim_param_id);
      if (res) {
        this.showSucceedSnackbar('Параметр удален');
        
        // Обновляем данные из БД без loader (реактивное обновление)
        const data = await getBimParams();
        if (data) {
          this.params = data;
        }
      }
    },
    async onConfirmDelete() {
      this.confirmDialog.show = false;
      if (this.confirmDialog.onConfirm) {
        await this.confirmDialog.onConfirm();
      }
    },
    toggleParamSelection(paramId, selected) {
      if (selected) {
        if (!this.selectedParams.includes(paramId)) {
          this.selectedParams.push(paramId);
        }
      } else {
        const index = this.selectedParams.indexOf(paramId);
        if (index > -1) {
          this.selectedParams.splice(index, 1);
        }
      }
    },
    toggleAllSelection(selected) {
      if (selected) {
        this.selectedParams = this.params.map(p => p.bim_param_id);
      } else {
        this.selectedParams = [];
      }
    },
    confirmDeleteSelected() {
      this.confirmDialog = {
        show: true,
        title: 'Подтверждение',
        message: `Вы уверены, что хотите удалить ${this.selectedParams.length} параметров?`,
        onConfirm: async () => {
          await this.deleteSelected();
        }
      };
    },
    async deleteSelected() {
      this.deletionProgress = {
        current: 0,
        total: this.selectedParams.length
      };
      this.deletionProgressDialog = true;

      const paramsToDelete = [...this.selectedParams];
      
      for (const paramId of paramsToDelete) {
        await deleteBimParam(paramId);
        this.deletionProgress.current++;
      }

      this.deletionProgressDialog = false;
      this.selectedParams = [];
      this.showSucceedSnackbar(`Удалено ${paramsToDelete.length} параметров`);
      
      // Обновляем данные из БД без loader (реактивное обновление)
      const res = await getBimParams();
      if (res) {
        this.params = res;
      }
    },
  },
  mounted() {
    this.loadData();
  },
  setup() {
    const { showSucceedSnackbar } = useCustomSnackbar();
    return { showSucceedSnackbar };
  },
};
</script>

<style scoped lang="scss">
.bim-params {
  padding: 8px;
}

.top-bar {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;

  :deep(.title) {
    margin-bottom: 0 !important;
  }
}

.header {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
  justify-content: space-between;
  align-items: end;

  &__info {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__count {
    font-size: 14px;
    color: #666;
    font-weight: 500;
  }

  &__create-btn {
    width: auto;
    height: 46px;
  }

  &__create-btn-icon {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: #fff;
    color: #0060fe;
  }

  :deep(.q-btn__content) {
    display: flex;
    align-items: center;
    gap: 16px;
  }
}

.action-buttons {
  display: flex;
  gap: 4px;
  justify-content: center;
}

/* Стили модального окна */
.param-modal {
  min-width: 500px;
  max-width: 700px;
}

.param-modal__header {
  padding: 12px 16px;
  background-color: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
}

.param-modal__title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  flex-grow: 1;
}

.param-modal__close {
  color: #666;
}

.param-modal__content {
  padding: 16px;
}

.param-form {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.param-form__section {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.param-form__item {
  width: 100%;
}

.param-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #e0e0e0;
}

.param-modal__footer :deep(button) {
  width: auto;
  min-width: 100px;
  height: 36px;
}
</style>

